import { FocusEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { Popover, Stack } from '@mantine/core';
import { DatePicker } from '@mantine/dates';
import { HiOutlineCalendarDays, HiXMark } from 'react-icons/hi2';
import { clsx } from 'clsx';
import dayjs from '../../../lib/dayjs';
import MaskedTimeInput from '../../../atoms/MaskedTimeInput';
import { usePortalTarget } from '../../../provider/UiContext';
import { hasSecondsPart, hasTimePart } from '../../dataTableFilters';
import { DTB_POPUP_Z } from '../../popupZIndex';

interface IDataTableDateTimeFilterProps {
    value: string;
    onChange: (value: string) => void;
    onSubmit?: () => void;
    label: string;
    placeholder: string;
    withSeconds: boolean;
    loupe: boolean;
    withinPortal?: boolean;
}

/** Дата без времени остаётся днём; время сравнивается с точностью записи (минута или секунда), включая полночь.
 *  Набранный текст применяется по Enter и уходу из поля, выбор в календаре и поле времени — сразу. */
const DataTableDateTimeFilter = ({
    value,
    onChange,
    onSubmit,
    label,
    placeholder,
    withSeconds,
    loupe,
    withinPortal = true,
}: IDataTableDateTimeFilterProps) => {
    const portalTarget = usePortalTarget();
    const timeFormat = withSeconds ? 'HH:mm:ss' : 'HH:mm';
    // Точность значения, а не колонки: «12:30» в колонке с секундами — минута, и календарь её не уточняет.
    const valueTimeFormat = hasSecondsPart(value) ? 'HH:mm:ss' : 'HH:mm';
    const format = hasTimePart(value) ? `DD.MM.YYYY ${withSeconds ? valueTimeFormat : 'HH:mm'}` : 'DD.MM.YYYY';
    const formatted = value ? dayjs(value).format(format) : '';
    const [text, setText] = useState(formatted);
    const [opened, setOpened] = useState(false);
    const [pendingTime, setPendingTime] = useState('');
    const editorRef = useRef<HTMLSpanElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const date = value ? dayjs(value).format('YYYY-MM-DD') : null;
    const time = hasTimePart(value) ? dayjs(value).format(timeFormat) : pendingTime;
    const valueTime = hasTimePart(value) ? dayjs(value).format(valueTimeFormat) : pendingTime;

    useEffect(() => {
        setText(formatted);
        setPendingTime('');
    }, [formatted]);

    const commit = () => {
        // Фокус/blur не переписывает сохранённую зону, секунды или доли секунды.
        if (text === formatted) return;
        const trimmed = text.trim();
        if (!trimmed) {
            if (value) onChange('');
            return;
        }
        const formats = ['DD.MM.YYYY', 'DDMMYYYY', 'YYYY-MM-DD', 'DD.MM.YYYY HH:mm', 'YYYY-MM-DDTHH:mm'];
        if (withSeconds) formats.push('DD.MM.YYYY HH:mm:ss', 'YYYY-MM-DDTHH:mm:ss');
        const parsed = dayjs(trimmed, formats, true);
        if (!parsed.isValid()) {
            setText(formatted);
            return;
        }
        const typedTime = trimmed.includes(':');
        const day = parsed.format('YYYY-MM-DD');
        // Время, выбранное в поповере до даты, видно в поле времени — дата без времени получает его.
        const next = typedTime
            ? parsed.format(`YYYY-MM-DD[T]${(trimmed.match(/:/g)?.length ?? 0) > 1 ? 'HH:mm:ss' : 'HH:mm'}`)
            : pendingTime
              ? `${day}T${pendingTime}`
              : day;
        setPendingTime('');
        if (next !== value) onChange(next);
        else setText(formatted);
    };

    const open = () => setOpened(true);
    const isInside = (node: EventTarget | null) =>
        node instanceof Node && (!!editorRef.current?.contains(node) || !!dropdownRef.current?.contains(node));
    const closeOnLeave = (event: FocusEvent) => {
        if (!isInside(event.relatedTarget)) setOpened(false);
    };

    const onKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter') {
            event.preventDefault();
            commit();
            setOpened(false);
            onSubmit?.();
        } else if (event.key === 'Escape') {
            if (opened) event.preventDefault();
            setOpened(false);
            setText(formatted);
            setPendingTime('');
        } else if (event.key === 'ArrowDown' && !opened) {
            event.preventDefault();
            open();
        }
    };

    return (
        <Popover
            opened={opened}
            onChange={setOpened}
            position="bottom-start"
            withinPortal={withinPortal}
            zIndex={DTB_POPUP_Z}
            portalProps={portalTarget ? { target: portalTarget } : undefined}
        >
            <Popover.Target>
                <span className="dtb__feditor" ref={editorRef} onBlur={closeOnLeave}>
                    <input
                        className={clsx('dtb__finput-field', loupe && 'has-loupe', 'has-clear')}
                        value={text}
                        placeholder={placeholder}
                        aria-label={label}
                        title={formatted || `дд.мм.гггг ${withSeconds ? 'чч:мм:сс' : 'чч:мм'}`}
                        onChange={(event) => setText(event.target.value)}
                        onBlur={commit}
                        onKeyDown={onKeyDown}
                        onClick={open}
                    />
                    <button
                        type="button"
                        className="dtb__fclear"
                        aria-label={
                            value ? `Очистить ${label.toLocaleLowerCase('ru-RU')}` : `Выбрать дату и время: ${label}`
                        }
                        title={value ? 'Очистить фильтр' : 'Выбрать дату и время'}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => {
                            if (value) {
                                setText('');
                                setPendingTime('');
                                onChange('');
                            } else setOpened((prev) => !prev);
                        }}
                    >
                        {value ? <HiXMark aria-hidden /> : <HiOutlineCalendarDays aria-hidden />}
                    </button>
                </span>
            </Popover.Target>
            <Popover.Dropdown ref={dropdownRef} data-dates-dropdown p="sm" tabIndex={-1} onBlur={closeOnLeave}>
                <Stack gap="sm">
                    <DatePicker
                        value={date}
                        defaultDate={date ?? undefined}
                        onChange={(next) => {
                            if (next) onChange(valueTime ? `${next}T${valueTime}` : next);
                        }}
                    />
                    <MaskedTimeInput
                        value={time}
                        withSeconds={withSeconds}
                        aria-label={`${label}, время`}
                        placeholder="Весь день"
                        onChange={(next) => {
                            if (next === time) return;
                            if (date) onChange(next ? `${date}T${next}` : date);
                            else setPendingTime(next);
                        }}
                        popoverProps={{ withinPortal: false }}
                    />
                </Stack>
            </Popover.Dropdown>
        </Popover>
    );
};

export default DataTableDateTimeFilter;
